import { GlobalRole, SearchOrder } from '@itm/shared-frontend/lib/types';
import { TanstackTableSearchParamsAdapter } from '@itm/shared-frontend/lib/utils';
export * from './dataGateway';
export * from './auth';
export * from './notification';

export type {
  GlobalRole,
  TableSortParams,
  TenantBrandingResponse,
  SelectOption,
  ServerValidationError,
  ServerValidationResponse,
  ServerError,
  FieldErrors,
  ServerFormErrors,
  ExternalFilters,
  TanstackTableChangeParams,
} from '@itm/shared-frontend/lib/types';

export enum TargetPlatform {
  AWS = 'AWS',
  AZURE = 'AZURE',
}

export enum RoleWithoutPrefix {
  SuperAdmin = 'SuperAdmin',
  Support = 'Support',
  Analyst = 'Analyst',
  Viewer = 'Viewer',
  ClientAdmin = 'ClientAdmin',
  EmployerAdmin = 'EmployerAdmin',
  Manager = 'Manager',
  CustomerOperations = 'CustomerOperations',
  SftpOnly = 'SftpOnly',
}

export enum ClientPortalRole {
  SuperAdmin = 'SuperAdmin',
  Support = 'Support',
  Analyst = 'ClientPortal_Analyst',
  Viewer = 'ClientPortal_Viewer',
  ClientAdmin = 'ClientPortal_ClientAdmin',
  EmployerAdmin = 'ClientPortal_EmployerAdmin',
  Manager = 'ClientPortal_Manager',
  CustomerOperations = 'ClientPortal_CustomerOperations',
  SftpOnly = 'ClientPortal_SftpOnly',
}

export enum DataGatewayRole {
  SuperAdmin = 'SuperAdmin',
  Support = 'Support',
  Analyst = 'DataGateway_Analyst',
  Viewer = 'DataGateway_Viewer',
  ClientAdmin = 'DataGateway_ClientAdmin',
  Manager = 'DataGateway_Manager',
}

export enum UserStatus {
  Invited = 'Invited',
  Active = 'Active',
  Deactivated = 'Deactivated',
  SetupIncomplete = 'SetupIncomplete',
}

export enum UserSQLPermissions {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export enum ContactPreference {
  Website = 'Website',
  Email = 'Email',
  Phone = 'Phone',
  Mail = 'Mail',
}
export enum PhoneNumberType {
  Main = 'Main',
  NonUK = 'NonUK',
  SMS = 'SMS',
  Welsh = 'Welsh',
  WhatsApp = 'WhatsApp',
}

export enum CompanyStatus {
  None = 'None',
  SchemesAvailable = 'SchemesAvailable',
  ProductsAvailable = 'ProductsAvailable',
}

export enum DataSeparationType {
  Default = 'Default',
  Company = 'Company',
}

export enum PaymentMethod {
  DirectDebit = 'DirectDebit',
  NoDirectDebit = 'NoDirectDebit',
}

export enum BulkUploadAction {
  Update = 'Update',
  Create = 'Create',
}

export enum BulkUploadArea {
  Company = 'Company',
  Scheme = 'Scheme',
  Employer = 'Employer',
  StrategicPartner = 'StrategicPartner',
  User = 'User',
  Section = 'Section',
}

export enum SourceFileStatus {
  InProgress = 'InProgress',
  Uploaded = 'Uploaded',
  Completed = 'Completed',
  Failed = 'Failed',
}

export enum Severity {
  Error = 'Error',
  Warning = 'Warning',
  Info = 'Info',
}

export enum SourceFileAction {
  None = 'None',
  Create = 'Create',
  Update = 'Update',
}

export enum SourceFileArea {
  None = 'None',
  User = 'User',
  Company = 'Company',
  Scheme = 'Scheme',
  Employer = 'Employer',
  Section = 'Section',
  StrategicPartner = 'StrategicPartner',
}

export enum CreatedStatus {
  Created = 'Created',
  NotCreated = 'NotCreated',
  Updated = 'Updated',
  PartiallyCreated = 'PartiallyCreated',
}

export enum UserAccountState {
  AccountCreated = 'AccountCreated',
  MFAReset = 'MFAReset',
  PasswordChanged = 'PasswordChanged',
}

export type CompanyResponse = {
  id: string;
  tenantId: string;
  name: string | null;
  statuses: CompanyStatus[];
  logoUrl: string | null;
  database: DataSeparationType;
  updatedDate: string | null;
  createdDate: string;
};

export type AdminContactResponse = {
  id: string;
  displayName: string | null;
  administratorName: string | null;
  administratorReference: string | null;
  website: string | null;
  email: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  address4: string | null;
  address5: string | null;
  postcode: string | null;
  postalName: string | null;
  countryCode: string | null;
  createdDate: string;
  updatedDate: string | null;
  contactPreference: ContactPreference;
  administratorPhones: AdministratorPhoneResponse[] | null;
};

export type CompanyAdminDataSourceResponse = {
  id: string;
  adminDataSourceId: string;
  name: string;
  isLinkedToScheme: boolean;
};

export type CompanyDetailsResponse = CompanyResponse & {
  accessToAllSchemesEnabled: boolean;
  sourceId: number | null;
  adminContact: AdminContactResponse;
  alternativeAdminContact: AdminContactResponse | null;
  companyAdminDataSources: CompanyAdminDataSourceResponse[] | null;
};

export type AuditResponse = {
  id: string;
  userName: string;
  userId: string;
  eventType: string;
  entityType: string;
  dateTime: string;
  oldValues: string;
  newValues: string;
  affectedColumn: string;
  entityId: string;
};

export type GetAuditResponse = {
  totalCount: number;
  auditRecords: AuditResponse[];
};

export type CompanySearchResponse = {
  totalCount: number;
  companies: CompanyResponse[];
};

export type AdministratorPhoneDto = {
  id?: string | null;
  phoneNumber: string | null;
  order?: number;
  phoneNumberType: PhoneNumberType;
};

export enum AdminContactType {
  Primary = 'Primary',
  Alternative = 'Alternative',
}

export type AdminContactModel = {
  displayName?: string;
  administratorName?: string;
  administratorReference?: string;
  contactPreference?: ContactPreference;
  website?: string;
  email?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  address5?: string;
  postcode?: string;
  postalName?: string;
  countryCode?: string;
  administratorPhones?: AdministratorPhoneDto[];
};

export type CreateCompanyModel = {
  companyName: string;
  tenantId: string;
  dataSeparationRequired: boolean;
  sourceId?: number;
  adminDataSourceNames: string[];
  adminContact: AdminContactModel;
  alternativeAdminContact?: AdminContactModel;
};

export type UpdateCompanyModel = Omit<CreateCompanyModel, 'dataSeparationRequired'> & {
  companyId: string;
};

export type AddProductPermissionModel = {
  userId: string;
  companyId: string;
  schemeId: string;
  productId: string;
  productRoleId: string;
};

export type AddEmployerPermissionModel = {
  companyId: string;
  userId: string;
  employerId: string;
};

export type BulkAddEmployerPermissionModel = {
  companyId: string;
  userId: string;
  employerIds: string[];
};

export type BulkRemoveEmployerPermissionModel = {
  userEmployerIds: string[];
};

export type SchemeUserEmployersModel = {
  companyId: string;
  userId: string;
  schemeId: string;
};

export type SchemeIdModel = {
  id: string;
};

export type ProductIdModel = {
  id: string;
  schemes?: SchemeIdModel[];
};

export type CompanyIdModel = {
  id: string;
  products?: ProductIdModel[];
};

export type AddBulkPermissionModel = {
  userId: string;
  companies: CompanyIdModel[];
};

export type DeletePermissionModel = {
  userId: string;
  companyId: string;
  productId: string;
  userProductRoleId: string;
  schemeId: string;
};

export type UpdatePermissionModel = {
  userId: string;
  companyId: string;
  productId: string;
  productRoleId: string;
};

export type BasePermissionModel = {
  userId: string;
  companyId: string;
  productId: string;
};

export type PermissionModel = {
  userId: string;
  companyId: string;
  productId: string;
  schemeId: string;
};

export type SchemeIdDto = {
  id: string;
};

export type ProductIdDto = {
  productId: string;
  schemes: SchemeIdDto[];
};

export type CompanyIdDto = {
  id: string;
  products: ProductIdDto[];
};

export type BulkAssignSchemeProductPermissions = {
  userId: string;
  company: CompanyIdDto;
};

export type BulkAssignEmployerSchemeProductPermissions = {
  userId: string;
  schemeId: string;
};

export type BulkAssignUserProductModel = {
  userId: string;
  companyId: string;
  productIds: string[];
};

export type UserPermissionResponse = {
  userId: string;
  companyId: string;
  companyName: string;
  schemeId: string;
  schemeName: string;
  productId: string;
  productName: string;
  productRoleId: string;
  productRoleName: GlobalRole;
  userProductRoleId: string;
  assignmentDate: string;
};

export type UserEmployerPermissionResponse = {
  userEmployerId: string;
  userId: string;
  employerId: string;
};

export type CreateSupportUserModel = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile?: string;
};

export type CreateUserModel = CreateSupportUserModel & {
  companyId: string;
};

export type CheckUserModel = {
  email: string;
};

export type AddClientPortalModel = {
  userId: string;
  clientPortalRoleId: string;
};

export type UserDto = {
  id: string;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  mobile: string | null;
  status: UserStatus;
  updatedDate: string;
  createdDate: string;
};

export type UserRoleResponse = {
  productRoleId: string;
  roleName: ClientPortalRole | null;
};

export type CurrentUserResponse = {
  hasSFTPAccess: boolean;
  companies: CompanyResponse[];
  clientPortalRole: UserRoleResponse;
  tenants: TenantResponse[];
  userSshKey: UserSshKeyResponse;
} & UserDto;

export type CompanyAccessResponse = {
  id: string;
  hasDefaultSchemeAccess: boolean;
};

export type UserSshKeyResponse = {
  hasPublicKey: boolean;
  createdDate: string | null;
  updatedDate: string | null;
};

export type UserDetailResponse = {
  hasSFTPAccess: boolean;
  companies: CompanyAccessResponse[];
  clientPortalRole: UserRoleResponse;
  accountState: UserAccountState;
  userSshKey: UserSshKeyResponse;
} & UserDto;

export type UserAuthChallenge = {
  name: string;
  response: string;
};

export type UserAuthEvent = {
  eventType: string | null;
  eventResult: string | null;
  device: string | null;
  ip: string | null;
  createDate: string | null;
};

export type GetUserDetailsResponseDto = {
  userIdentityId: string | null;
  userId: string | null;
  status: UserStatus | null;
  userAccountState: UserAccountState | null;
  roles: string | null;
  name: string | null;
  email: string | null;
  username: string | null;
  emailVerified: boolean;
  enabled: boolean;
  twoFactorEnabled: boolean;
  createdDate: string;
  updatedDate: string | null;
};

export type GetUserAuthEventsResponse = {
  totalCount: number;
  records: UserAuthEvent[] | null;
};

export type UserCompanyModel = {
  userId: string;
  companyId: string;
};

export type RequestSearchParams = {
  take?: number;
  skip?: number;
  search?: string;
  orderField?: string;
  orderBy?: SearchOrder;
};

export type UserSearchResponse = {
  hasSFTPAccess: boolean;
  clientPortalRole?: UserRoleResponse;
  canBeDeleted: boolean;
} & UserDto;

export type UserSearchPagedResponse = {
  totalCount: number;
  items: UserSearchResponse[];
};

export type UpdateUserModel = {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  mobile?: string;
  email: string;
};

export type ProductSchemeDto = {
  id: string;
  assignmentDate: string;
  schemeName: string;
  companyId: string;
};

export type ProductRolesDto = {
  id: string;
  name: string;
};
export type CompanyProductDto = {
  id: string;
  companyId: string;
};
export type UserProductsDto = {
  id: string;
  userId: string;
  schemeId: string;
};

export type ProductRoleResponse = {
  productRoleId: string;
  roleName: string;
};

export type ProductResponse = {
  id: string;
  name: string | null;
  domainPrefix: string | null;
  logoUrl: string | null;
  url: string | null;
  description: string | null;
  updatedDate: string;
  createdDate: string;
};

export type ShortSchemeResponse = {
  id: string;
  name: string;
};

export type ProductSchemeResponse = {
  id: string;
  name: string | null;
  logoUrl: string | null;
  schemes: ShortSchemeResponse[];
};

export type TenantProductResponse = {
  id: string;
  name: string | null;
  logoUrl: string | null;
  url: string | null;
  description: string | null;
  assigned: boolean;
  promote: boolean;
  canUnassign: boolean;
  domainPrefix: string | null;
};

export type CreateProductModel = {
  id: string;
  productName: string;
  description: string;
};

export type UpdateProductModel = {
  productId: string;
  productName: string;
  description: string;
};

export type AddRoleModel = {
  productId: string;
  roleName: string;
};

export type SchemeProductModel = {
  schemeId: string;
  productId: string;
};

export type CompanyProductModel = {
  companyId: string;
  productId: string;
};

export enum AdditionalSchemeType {
  OTH = 'OTH',
  FOR = 'FOR',
}

export type SchemeResponse = {
  id: string;
  companyId: string;
  schemeSrcId: string | null;
  additionalSourceId: number | null;
  name: string;
  friendlyName: string | null;
  createdDate: string;
  updatedDate: string | null;
  adminContact: AdminContactResponse | null;
  alternativeAdminContact: AdminContactResponse | null;
  urlSip: string | null;
  urlCostsandcharges: string | null;
  urlImplstatement: string | null;
  urlAnnualreport: string | null;
  psrNumber: number | null;
  pstrNumber: string | null;
  additionalName: string | null;
  additionalType: AdditionalSchemeType | null;
};

export type SchemeDetailsResponse = SchemeResponse & {
  pensionLink: string | null;
  companyAdminDataSourceId: string | null;
  adminDataSourceName: string | null;
};

export type SchemeAssignedToCompanyResponse = {
  id: string;
  companyId: string;
  name: string | null;
  friendlyName: string | null;
  additionalName: string | null;
  adminDataSourceName: string | null;
  schemeSrcId: string | null;
  contactPreference: ContactPreference | null;
  updatedDate: string | null;
  createdDate: string;
};

export type CreateSchemeModel = {
  companyId: string;
  schemeSrcId?: string;
  additionalSourceId?: number;
  pensionLink?: string;
  companyAdminDataSourceId: string;
  name: string;
  friendlyName?: string;
  adminContact?: AdminContactModel;
  alternativeAdminContact?: AdminContactModel | null;
  urlCostsandcharges?: string;
  urlSip?: string;
  urlImplstatement?: string;
  urlAnnualreport?: string;
  psrNumber?: number;
  pstrNumber?: string;
  additionalName?: string;
  additionalType?: AdditionalSchemeType;
};

export type UpdateSchemeModel = Omit<CreateSchemeModel, 'companyId'> & {
  schemeId: string;
};

export enum PensionType {
  AVC = 'AVC',
  CB = 'CB',
  CDC = 'CDC',
  DB = 'DB',
  DC = 'DC',
  HYB = 'HYB',
}

export enum PensionStatus {
  IWU = 'IWU',
  IPPF = 'IPPF',
}

export type SectionResponse = {
  id: string;
  schemeId: string;
  name: string | null;
  pensionType: PensionType;
  pensionStatus: PensionStatus | null;
  sectionSrcId: string | null;
  urlCostsandcharges: string | null;
  urlSip: string | null;
  urlImplstatement: string | null;
  urlAnnualreport: string | null;
  adminContact: AdminContactResponse | null;
  alternativeAdminContact: AdminContactResponse | null;
};

export type CreateSectionModel = {
  name: string;
  schemeId: string;
  sectionSrcId: string;
  pensionType: PensionType;
  pensionStatus?: PensionStatus;
  urlCostsandcharges?: string;
  urlSip?: string;
  urlImplstatement?: string;
  urlAnnualreport?: string;
  adminContact?: AdminContactModel;
  alternativeAdminContact?: AdminContactModel;
};

export type UpdateSectionModel = Omit<CreateSectionModel, 'schemeId'> & {
  id: string;
};

export type SectionExtendedResponse = {
  id: string;
  name: string | null;
  pensionType: PensionType;
  pensionStatus: PensionStatus;
  dataSourceName: string | null;
  sectionSrcId: string | null;
  contactPreference: ContactPreference;
  createdDate: string;
  updatedDate: string | null;
};

export type SectionExtendedSearchResponse = {
  totalCount: number;
  items: SectionExtendedResponse[];
};

export type ContactInfoResponse = {
  email: string;
};

export type ClientPortalInfoResponse = {
  version: string | null;
  description?: string;
};

export type AdminDataSourceResponse = {
  id: string;
  name: string;
};

export type AdministratorPhoneResponse = {
  id: string;
  administratorId: string;
  order: number;
  phoneNumber: string;
  phoneNumberType: PhoneNumberType;
};

export type AdministratorPhoneModel = {
  phoneNumber: string;
  phoneNumberType: PhoneNumberType;
};

export enum TenantStatus {
  None = 'None',
  DetailsIncomplete = 'DetailsIncomplete',
  BrandingIncomplete = 'BrandingIncomplete',
  AssignProductsIncomplete = 'AssignProductsIncomplete',
  Available = 'Available',
}

export type TenantResponse = {
  id: string;
  name: string | null;
  domain: string | null;
  logoUrl: string | null;
};

export type TenantShortResponse = {
  id: string;
  name: string | null;
};

export type DomainResponse = {
  id: string;
  name: string | null;
};

export type CreateTenantDto = {
  name: string;
  domain: string;
};

export type UpdateTenantDto = {
  name: string;
  domain: string;
};

export type UpdateTenantBrandingModel = {
  termsAndConditions?: string;
  colorPrimary: string;
  colorPrimaryActive: string;
  colorPrimaryHover: string;
  colorPrimaryInvert: string;
  colorSecondary: string;
  colorSecondaryActive: string;
  colorSecondaryHover: string;
  colorSecondaryInvert: string;
};

export type UpdateTenantProductDto = {
  promote: boolean;
};

export type TenantSearchResponse = {
  id: string;
  name: string | null;
  domain: string | null;
  status: TenantStatus;
  createdDate: string;
  updatedDate: string | null;
};

export type TenantSearchPagedResponse = {
  totalCount: number;
  items: TenantSearchResponse[];
};

export type EmployerResponse = {
  id: string;
  name: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  address5: string;
  postcode: string;
  email: string;
  hasPaymentPlan: boolean;
  schemeId: string;
  sectionId: string | null;
  strategicPartnerId: string | null;
  updatedDate: string | null;
  createdDate: string;
  epsr: string;
  sectionName: string;
  paymentMethod: string | null;
  companiesHouseNumber: string;
  charityNumber: string;
  schemeName: string;
  strategicPartnerName: string;
  externalId: string | null;
  hasMultipleSchemes: boolean;
};

export type SchemeSectionStrategicPartnerEmployerResponse = {
  schemeId: string;
  schemeName: string | null;
  sectionId: string | null;
  sectionName: string | null;
  strategicPartnerId: string | null;
  strategicPartnerName: string | null;
  isSectionRequired: boolean;
};

export type EmployersDetailedResponse = {
  id: string;
  name: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  address4: string | null;
  address5: string | null;
  postcode: string | null;
  email: string | null;
  hasPaymentPlan: boolean;
  updatedDate: string | null;
  createdDate: string;
  epsr: string | null;
  paymentMethod: PaymentMethod | null;
  companiesHouseNumber: string | null;
  charityNumber: string | null;
  externalId: string | null;
  poNumber: string | null;
  parentEmployerId: string | null;
  parentEmployerName: string | null;
  schemeSectionStrategicPartners: SchemeSectionStrategicPartnerEmployerResponse[] | null;
};

export type EmployerDetailsByEmployerAndSchemeModel = {
  employerId: string;
  schemeId: string;
};

export type UpdateEmployerModel = {
  companyId: string;
  id: string;
  name: string;
  address1: string;
  address2?: string;
  address3?: string;
  address4?: string;
  address5?: string;
  postcode: string;
  email?: string;
  hasPaymentPlan: boolean;
  paymentMethod?: PaymentMethod;
  companiesHouseNumber?: string;
  charityNumber?: string;
  externalId?: string;
  poNumber?: string;
  parentEmployerId?: string;
  schemeSectionStrategicPartnerEmployers?: SchemeSectionStrategicPartnerEmployerDto[];
};

export type SchemeSectionStrategicPartnerEmployerDto = {
  schemeId: string;
  sectionId?: string;
  strategicPartnerId?: string;
};

export type CreateEmployerModel = {
  companyId: string;
  epsr?: string;
  name: string;
  email: string;
  paymentMethod?: PaymentMethod;
  companiesHouseNumber?: string;
  charityNumber?: string;
  address1: string;
  address2?: string;
  address3?: string;
  address5?: string;
  address4?: string;
  postcode: string;
  hasPaymentPlan: boolean;
  poNumber?: string;
  parentEmployerId?: string;
  externalId?: string;
  schemeSectionStrategicPartnerEmployers: SchemeSectionStrategicPartnerEmployerDto[];
};

export type ShortEmployerResponse = {
  id: string;
  name: string | null;
};

export type StrategicPartnerResponse = {
  id: string;
  name: string | null;
};

export type CreateStrategicPartnerModel = {
  name: string;
  schemeId: string;
  sourceId?: string;
};

export type UpdateStrategicPartnerModel = {
  id: string;
  schemeId: string;
  name: string;
};

export type SectionBySchemeIdResponse = {
  schemeId: string;
  schemeName: string;
};

export type SearchSourceFileResponse = {
  sourceFileId: string;
  fileName: string | null;
  area: SourceFileArea;
  action: SourceFileAction;
  actionText: string | null;
  areaText: string | null;
  uploadedDate: string;
  uploadedBy: string | null;
  status: SourceFileStatus;
  statusText: string | null;
};

export type SearchSourceFileResponsesWithCount = {
  totalCount: number;
  items: SearchSourceFileResponse[];
};

export type SourceFileResponse = {
  id: string;
  area: SourceFileArea | null;
  name: string | null;
  bulkUploadStatuses: BulkUploadStatusDto[];
};

export type BulkUploadStatusDto = {
  id: string;
  companyId: string | null;
  companyName: string | null;
  employerName: string | null;
  epsr: string | null;
  schemeName: string | null;
  sectionName: string | null;
  strategicPartnerName: string | null;
  status: CreatedStatus | null;
};

export type SectionUploadStatusDto = {
  companyName: string | null;
  schemeName: string | null;
  adminDataSource: string | null;
  sectionSrcId: string | null;
  sectionName: string | null;
  hasError: boolean;
  messages: string | null;
  status: string | null;
};

export type SchemeUploadStatusDto = {
  companyName: string | null;
  schemeName: string | null;
  adminDataSource: string | null;
  schemeSrcId: string | null;
  hasError: boolean;
  messages: string | null;
  status: string | null;
};

export type EmployerUploadStatusDto = {
  companyName: string | null;
  schemeName: string | null;
  employerName: string | null;
  epsr: string | null;
  hasError: boolean;
  messages: string | null;
  status: string | null;
};

export type UpdateSectionUploadStatusDto = {
  companyName: string | null;
  schemeName: string | null;
  adminDataSource: string | null;
  sectionSrcId: string | null;
  sectionName: string | null;
  hasError: boolean;
  messages: string | null;
  status: string | null;
};

export type CompanyUploadStatusDto = {
  companyName: string | null;
  companyId: string | null;
  tenantName: string | null;
  adminDataSource: string | null;
  hasError: boolean;
  messages: string | null;
  status: string | null;
};

export type UserUploadStatusDto = {
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  schemeName: string | null;
  hasError: boolean;
  messages: string | null;
  status: string | null;
};

export type StrategicPartnerStatusDto = {
  schemeName: string | null;
  strategicPartnerName: string | null;
  hasError: boolean;
  messages: string | null;
  status: string | null;
};

export enum DataAccessDurationType {
  Hours = 'Hours',
  Days = 'Days',
}

export enum UserDataPermissionStatus {
  Pending = 'Pending',
  Active = 'Active',
  Expired = 'Expired',
  Rejected = 'Rejected',
  Revoked = 'Revoked',
}
export enum UserDataPermissionRequestStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Revoked = 'Revoked',
}

export type DataAccessPermissionPagedResponse = {
  totalCount: number;
  items: DataAccessPermissionResponse[];
};

export type DataAccessPermissionResponse = {
  companyId: string;
  companyName: string | null;
  userDataPermissionRequestId: string;
  userDataPermissionId: string;
  reviewerUserId: string | null;
  reviewerUserFullName: string | null;
  status: UserDataPermissionStatus;
  reviewDate: string | null;
  duration: number;
  isExtendedRequest: boolean;
  expiryDate: string | null;
};

export type GetDataAccessDetailsRequestModel = {
  id: string;
  reviewerName: string | null;
  requestDate: string;
  reviewDate: string | null;
  requestDescription: string | null;
  reviewDescription: string | null;
  duration: number;
  status: UserDataPermissionRequestStatus;
  expiryDate: string | null;
  sentTo: string | null;
};

export type GetDataAccessDetailsSchemeModel = {
  id: string;
  name: string | null;
};

export type GetDataAccessDetailsProductModel = {
  id: string;
  productName: string | null;
  schemes: GetDataAccessDetailsSchemeModel[];
};

export type GetDataAccessDetailsExtendRequestModel = {
  id: string;
  requestDate: string;
  sentTo: string | null;
  requestDescription: string | null;
  reviewerName: string | null;
  reviewDate: string | null;
  status: UserDataPermissionRequestStatus;
  duration: number;
  expiryDate: string | null;
  reviewDescription: string | null;
};

export type GetDataAccessDetailsResponse = {
  companyName: string | null;
  createdBy: string | null;
  accessExpiryDate: string;
  status: UserDataPermissionStatus;
  requests: GetDataAccessDetailsRequestModel[] | null;
  products: GetDataAccessDetailsProductModel[] | null;
  extendRequest: GetDataAccessDetailsExtendRequestModel | null;
};

export enum DataAccessRequestStatusDto {
  Pending = 'Pending',
  Rejected = 'Rejected',
  Active = 'Active',
  Revoked = 'Revoked',
  Expired = 'Expired',
}

export enum UserDataPermissionRequestType {
  New = 'New',
  Extend = 'Extend',
}

export type ExtensionRequestDetails = {
  requestDate: string;
  sentTo: string | null;
  reason: string | null;
  reviewerName: string | null;
  reviewDate: string | null;
  status: UserDataPermissionRequestStatus;
  duration: number;
  expiryDate: string | null;
};

export type DataAccessRequestDto = {
  id: string;
  permissionId: string;
  accessRequestedBy: string | null;
  reviewerName: string | null;
  requestDate: string;
  expiryDate: string | null;
  reviewDate: string | null;
  totalHours: number;
  status: DataAccessRequestStatusDto;
  requestType: UserDataPermissionRequestType;
};

export type DataAccessPagedRequestDto = {
  totalCount: number;
  items: DataAccessRequestDto[];
};

export type GetDataAccessRequestDetailsResponse = {
  id: string;
  companyName: string | null;
  accessRequestedBy: string | null;
  status: UserDataPermissionRequestStatus;
  requestDate: string;
  reviewDate: string | null;
  expiryDate: string | null;
  duration: number;
  requestDescription: string | null;
  reviewDescription: string | null;
  reviewerName: string | null;
  sentTo: string | null;
  isExtendedRequest: boolean;
  extensionRequestDetails: ExtensionRequestDetails | null;
  products: GetDataAccessDetailsProductModel[] | null;
};

export type SchemeFromProductDto = {
  schemeId: string;
  schemeName?: string;
};

export type ProductDto = {
  productId: string;
  productName?: string;
  schemes: SchemeFromProductDto[];
};

export type CreateDataAccessDto = {
  companyId: string;
  companyName?: string;
  accessDuration: number;
  reason: string;
  products: ProductDto[];
};

export type ExtendDataAccessRequest = {
  accessDuration: number;
  reason: string;
};

export type ApproveDataAccessRequestDto = {
  totalHours?: number;
};

export type RejectDataAccessRequestDto = {
  reason: string;
};

export type RevokeDataAccessDto = {
  reason: string;
};

export enum DataRetentionPolicyStatus {
  Draft = 'Draft',
  Active = 'Active',
  ApprovalPending = 'ApprovalPending',
}
export enum DataRetentionPolicyType {
  Default = 'Default',
  Alternative = 'Alternative',
}

export type DataRetentionSchemeResponse = {
  id: string;
  name: string | null;
};

export type DataRetentionResponse = {
  id: string;
  versionId: string;
  name: string | null;
  status: DataRetentionPolicyStatus;
  type: DataRetentionPolicyType;
  version: number;
  createdDate: string;
  updatedDate: string | null;
  isEditable: boolean;
};

export type DataRetentionPagedResponse = {
  totalCount: number;
  items: DataRetentionResponse[] | null;
};

export enum DataRetentionProductRuleType {
  TimeFrame = 'TimeFrame',
  Files = 'Files',
}

export type DataRetentionProductResponse = {
  id: string;
  productId: string;
  order: number;
  name: string | null;
  schemes: DataRetentionSchemeResponse[];
  rules: DataRetentionProductRuleResponse[];
};

export type DataRetentionProductRuleResponse = {
  id: string;
  order: number;
  subCategoryOrder: number | null;
  dataRetentionCategory: string | null;
  dataRetentionSubCategory: string | null;
  type: DataRetentionProductRuleType;
  period: number;
  createdDate: string;
};

export enum DataRetentionPolicyApprovalStatus {
  Draft = 'Draft',
  Approved = 'Approved',
  Rejected = 'Rejected',
  ApprovalPending = 'ApprovalPending',
}

export type DataRetentionPolicyApprovalResponse = {
  dataRetentionPolicyVersionId: string;
  reviewerUserId: string | null;
  status: DataRetentionPolicyApprovalStatus;
  updatedDate: string | null;
  createdDate: string;
  reviewerComment: string | null;
};

export type DataRetentionDetailsResponse = {
  id: string;
  name: string | null;
  status: DataRetentionPolicyStatus;
  type: DataRetentionPolicyType;
  version: number;
  createdDate: string;
  updatedDate: string | null;
  createdBy: string | null;
  products: DataRetentionProductResponse[];
  previousVersion: DataRetentionDetailsResponse | null;
  dataRetentionPolicyApprovalRequests: DataRetentionPolicyApprovalResponse[];
  isEditable: boolean;
};

export type DataRetentionCategoryPeriodOption = {
  type: DataRetentionProductRuleType;
  defaultValue: number;
  isDefault: boolean;
};

export type DataRetentionCategoryResponse = {
  category: string | null;
  description: string | null;
  order: number;
  types: DataRetentionCategoryPeriodOption[] | null;
  subCategories: DataRetentionSubcategoryResponse[] | null;
};

export type DataRetentionVersionResponse = {
  id: string;
  createdBy: string | null;
  reviewedBy: string | null;
  policyStatus: DataRetentionPolicyStatus;
  approvalStatus: DataRetentionPolicyApprovalStatus;
  version: number;
  createdDate: string;
  reviewDate: string | null;
};

export type DataRetentionVersionPagedResponse = {
  totalCount: number;
  items: DataRetentionVersionResponse[];
};

export type AvailableDataRetentionProduct = {
  productId: string;
  productName: string;
  schemes: DataRetentionSchemeResponse[] | null;
  dataRetentionCategories: DataRetentionCategoryResponse[] | null;
};

export type DataRetentionProductRuleDto = {
  dataRetentionCategory: string;
  dataRetentionSubCategory: string | null;
  type: DataRetentionProductRuleType;
  period: number;
};

export type DataRetentionProductDto = {
  order: number;
  productId: string;
  schemeIds: string[];
  dataRetentionProductRules: DataRetentionProductRuleDto[];
};

export type CreateDataRetentionDto = {
  companyId: string;
  name: string;
  dataRetentionProducts: DataRetentionProductDto[];
};

export type UpdatePolicyNameDataRetentionDto = {
  id: string;
  companyId: string;
  name: string;
};

export type UpdateDataRetentionDto = {
  id: string;
  companyId: string;
  creatorComment?: string;
  dataRetentionProducts: DataRetentionProductDto[];
};

export enum DataRetentionAuditStatus {
  InProgress = 'InProgress',
  Succeed = 'Succeed',
  Failed = 'Failed',
}

export type DataRetentionAuditResponse = {
  id: string;
  status: DataRetentionAuditStatus;
  message: string | null;
  scheme: string | null;
  product: string | null;
  category: string | null;
  exception: string | null;
  startDate: string;
  endDate: string | null;
};

export type DataRetentionAuditPagedResponse = {
  totalCount: number;
  items: DataRetentionAuditResponse[];
};

export type UserSearchSFTPInfoResponse = {
  id: string;
  companyId: string;
  productId: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  productName: string | null;
  storageAccountName: string | null;
  containerName: string | null;
  localUserName: string | null;
  sftPlogin: string | null;
};

export type UserSearchSFTPInfoResponsePagedResponse = {
  totalCount: number;
  items: UserSearchSFTPInfoResponse[] | null;
};

export type UpdateSftpFoldersModel = {
  folders: string[];
};

export type UserCompanyAllSchemesAccessResponse = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
};

export type UserCompanyAllSchemesAccessResponsePagedResponse = {
  totalCount: number;
  items: UserCompanyAllSchemesAccessResponse[];
};

export enum CompanyDefaultSchemeAccessAction {
  DisabledFeature = 'DisabledFeature',
  EnabledFeature = 'EnabledFeature',
  RemovedAccess = 'RemovedAccess',
  GrantedAccess = 'GrantedAccess',
}

export type DefaultSchemeAccessAuditResponse = {
  id: string;
  changedByUserFullName: string | null;
  action: CompanyDefaultSchemeAccessAction;
  date: string;
  affectedUserEmail: string | null;
};

export type DefaultSchemeAccessAuditResponsePagedResponse = {
  totalCount: number;
  items: DefaultSchemeAccessAuditResponse[];
};

export enum CompanyAuditArea {
  NotDetermined = 'NotDetermined',
  Company = 'Company',
  Scheme = 'Scheme',
  Section = 'Section',
  Employer = 'Employer',
  StrategicPartner = 'StrategicPartner',
  ProductAssignment = 'ProductAssignment',
  SchemeProductAssignment = 'SchemeProductAssignment',
  BulkUpload = 'BulkUpload',
}

export enum CompanyAuditAction {
  NotDetermined = 'NotDetermined',
  Add = 'Add',
  Update = 'Update',
  Delete = 'Delete',
}

export type CompanyAuditResponse = {
  id: string;
  area: CompanyAuditArea;
  action: CompanyAuditAction;
  description: string | null;
  changedByUserFullName: string | null;
  date: string;
  schemes: ShortSchemeResponse[] | null;
};

export type CompanyAuditResponsePagedResponse = {
  totalCount: number;
  items: CompanyAuditResponse[];
};

export type CompanyAuditDetailResponse = CompanyAuditResponse & {
  oldValues: Record<string, any> | null;
  newValues: Record<string, any> | null;
};

export enum UserAuditArea {
  NotDetermined = 'NotDetermined',
  User = 'User',
  SshKey = 'SshKey',
  Products = 'Products',
  Companies = 'Companies',
  Employers = 'Employers',
}

export enum UserAuditAction {
  NotDetermined = 'NotDetermined',
  Add = 'Add',
  Update = 'Update',
  Delete = 'Delete',
}

export type UserAuditResponse = {
  id: string;
  userId: string;
  area: UserAuditArea;
  action: UserAuditAction;
  description: string | null;
  changedByUserFullName: string | null;
  userEmail: string | null;
  date: string;
  companyId: string | null;
  companyName: string | null;
};

export type UserAuditDetailResponse = Omit<UserAuditResponse, 'userId'> & {
  oldValues: Record<string, any> | null;
  newValues: Record<string, any> | null;
};

export type UserAuditResponsePagedResponse = {
  totalCount: number;
  items: UserAuditResponse[];
};

export type EnvironmentOptionsResponse = {
  id: string;
  isPublishConfigurationEnabled: boolean;
};

export type UpdateEnvironmentOptionsModel = {
  isPublishConfigurationEnabled: boolean;
};

export type EnvironmentConfigurationProductResponse = {
  productId: string;
  productName: string | null;
};

export type EnvironmentConfigurationResponse = {
  id: string;
  name: string | null;
  dataGatewayUrl: string | null;
  clientId: string | null;
  secretCode: string | null;
  environmentConfigurationProducts: EnvironmentConfigurationProductResponse[] | null;
};

export type EnvironmentConfigurationProductModel = {
  productId: string;
};

export type EnvironmentConfigurationModel = {
  id?: string;
  name: string;
  dataGatewayUrl: string;
  clientId: string;
  secretCode: string;
  environmentConfigurationProducts: EnvironmentConfigurationProductModel[];
};

export type TestConnectionResponse = {
  productId: string;
  productName: string | null;
  isSuccess: boolean;
  errorMessage: string | null;
};

export type UpdatePublicKeyRequest = {
  publicKey: string;
};

export type GetUsersModel = TanstackTableSearchParamsAdapter & {
  roles?: string[] | null;
  status?: UserStatus[] | null;
  hasSFTPAccess?: boolean;
  employerId?: string | null;
  excludeUserIds?: string[] | null;
};

export type SearchEmployerResponse = {
  id: string;
  employerName: string;
  companiesHouseNumber: string;
  paymentPlan: boolean;
  epsr: string;
  paymentMethod: string | null;
  schemes: ShortSchemeResponse[];
  email: string;
  isSectionRequired: boolean;
};

export type SearchEmployerResponsePagedResponse = {
  totalCount: number;
  items: SearchEmployerResponse[];
};

export type DataRetentionSubcategoryResponse = {
  subCategory: string | null;
  description: string | null;
  order: number;
  categoryId: string | null;
  types: DataRetentionCategoryPeriodOption[] | null;
};

export type DeleteCompanyAdminDataSourceModel = {
  comment: string;
};

export enum PgpKeyType {
  Decrypt = 'Decrypt',
  Encrypt = 'Encrypt',
}

export type PgpKeyProductDto = {
  productId: string;
  productName: string | null;
};

export type PgpKeyResponse = {
  id: string;
  companyId: string;
  companyName: string | null;
  name: string | null;
  type: PgpKeyType;
  createdDate: string;
  updatedDate: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  products: PgpKeyProductDto[] | null;
};

export type PgpKeyResponsePagedResponse = {
  totalCount: number;
  items: PgpKeyResponse[] | null;
};

export type CreatePgpKeyDto = {
  companyId: string;
  name: string;
  type: PgpKeyType;
};

export type UpdatePgpKeyDto = {
  id: string;
  name: string;
  type: PgpKeyType;
};

export enum ReportType {
  Default = 'Default',
  UserAccessSummary = 'UserAccessSummary',
  EmailLogs = 'EmailLogs',
  AccountEmailLogs = 'AccountEmailLogs',
}

export enum ReportStatus {
  InProgress = 'InProgress',
  Success = 'Success',
  Failed = 'Failed',
}

export type GenerateUserAccessSummaryReportRequest = {
  reportType: ReportType.UserAccessSummary;
};

export type GenerateEmailLogsReportRequest = {
  reportType: ReportType.EmailLogs;
  productIds: string[];
  dateFrom: string;
  dateTo: string;
};

export type GenerateAccountEmailLogsReportRequest = {
  reportType: ReportType.AccountEmailLogs;
  dateFrom: string;
  dateTo: string;
};

export type GenerateReportRequest =
  GenerateUserAccessSummaryReportRequest | GenerateEmailLogsReportRequest | GenerateAccountEmailLogsReportRequest;

export type ReportSearchResponse = {
  id: string;
  createdByUserId: string;
  createdByUserName: string | null;
  createdDate: string;
  reportType: ReportType;
  status: ReportStatus;
  isDownloadable: boolean;
};

export type ReportSearchResponsePagedResponse = {
  totalCount: number;
  items: ReportSearchResponse[] | null;
};

export type ReportDownloadAuditResponse = {
  downloadedByUserName: string | null;
  date: string;
};

export type ReportDownloadAuditResponsePagedResponse = {
  totalCount: number;
  items: ReportDownloadAuditResponse[] | null;
};
